"use client";

import moment from 'moment';
import * as duckdb from '@duckdb/duckdb-wasm';
import MainController from "./MainController";
import { runPipeline } from './Pipeline';
import embed from "vega-embed";
import { io, Socket } from "socket.io-client";
import { getAuth, signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged } from 'firebase/auth';
import { updateDoc, getFirestore, collection, doc, getDoc, getDocs, addDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';

import { ISCHART_STUDIO_GS, ISCHART_INFORMATICS_GS } from "../informatics/config";
import { getCategoryDomain, sortRowsByCategoryOrder } from "../lib/category-order";
import { defaultDatasetTab, getDatasetTabs } from "./dataset-tabs";

// Host ส่งชื่อ GCS bucket ผ่าน prop `config` (controller.config); ค่าที่ import
// เป็นค่า fallback เมื่อ host ไม่ได้ส่งมา
const informaticsGs = (controller: MainController) =>
  controller.config?.ISCHART_INFORMATICS_GS ?? ISCHART_INFORMATICS_GS;
const studioGs = (controller: MainController) =>
  controller.config?.ISCHART_STUDIO_GS ?? ISCHART_STUDIO_GS;

export type State = {
  width?: number,
  height?: number,
  loggedin?: boolean,
  duckLoaded?: boolean,
  datasetList?: any[],
  templateList?: any[],
  chartInfo?: any,
  datasetId?: string | null,
  dimList?: string[],
  filename?: string | null,
  agg?: string | null,
  selectedTab?: string | null,
  showChart?: boolean,
  filter?: string | null,
  colorValues?: string[];
  socketMessage?: any,
  allDivisionRead?: boolean,
  selectedDivision?: any,
  permitToken?: any
}

export const StateInitial: State = {
  width: typeof window !== 'undefined' ? window.innerWidth : 1200,
  height: typeof window !== 'undefined' ? window.innerHeight : 800,
  loggedin: false,
  duckLoaded: false,
  datasetList: [],
  templateList: [],
  chartInfo: {},
  datasetId: null,
  dimList: [],
  filename: null,
  agg: null,
  selectedTab: null,
  showChart: false,
  filter: null,
  colorValues: [],
  socketMessage: null,
  allDivisionRead: false,
  selectedDivision: null
}

// ── Row-shape types used by GaDashboard ──────────────────────────────────────
export type UserActivityRow = { date: string; users: number };
export type CountryRow      = { country: string; users: number };
export type OsRow           = { os: string; ym: string; users: number };
export type PageViewRow     = { page: string; users: number };

export type Event =
  { message: "DidMount", params: any }
  | { message: "DidUpdate", params: any }
  | { message: "WillUnmount", params: any }
  | { message: "Login", params: any }
  | { message: "Logout", params: any }
  | { message: "GetDatasetList", params: {} }
  | {
    message: "GetChartData",
    params: { datasetId: string, dimList: string[], setData: any }
  }
  | { message: "GetCustomChartData", params: { datasetId: string, filename: string, setData: any, prefix?: string } }
  | { message: "GetChartInfo", params: { datasetList: string[] } }
  | { message: "LoadChartInfoOne", params: { datasetId: string } }
  | { message: "GetTemplateList", params: {} }
  | { message: "AddTemplate", params: { template: any } }
  | {
    message: "GenInformatics",
    params: {
      arrayBuffer: ArrayBuffer, setProgress: any, setImageUrl: any, template: any
    }
  }
  | { message: "GetFields", params: { templateId: string, setFields: any } }
  | { message: "GetRecipe", params: { recipeId: string, setRecipe: any } }
  | { message: "AddRecipeItem", params: { item: any, setRecipe: any } }
  | {
    message: "GetGaDashboardData",
    params: {
      datasetId: string,
      setUserActivity: (data: UserActivityRow[]) => void,
      setTopCountries: (data: CountryRow[]) => void,
      setOsData:       (data: OsRow[]) => void,
      setPageViews:    (data: PageViewRow[]) => void,
    }
  }
  | {
    message: "GetMelabReportBundle",
    params: {
      datasetId: string,
      setBundle: (manifest: any) => void,
      setError: (msg: string) => void,
    }
  }
  | {
    message: "QueryMelabReport",
    params: { sql: string, setRows: (rows: any[]) => void, setError?: (msg: string) => void }
  }

export type Data = {
  duckInstance?: duckdb.AsyncDuckDB,
  duckCon?: duckdb.AsyncDuckDBConnection,
  socket: Socket | null,
}

export const DataInitial = {
  socket: null,
}


type Handler = (controller: MainController, params?: any) => any




export const DidMount: Handler = async (controller, params) => {
  // Strict Mode (dev) จำลอง mount -> unmount -> mount บน instance เดิม = controller ตัวเดิม
  // ถ้าไม่ reset ตรงนี้ unmounted จะค้างเป็น true ตั้งแต่รอบสอง แล้ว setState ถูกทิ้งทั้งหมด
  // (อาการ: dataset ไม่ขึ้นเลยตอน next dev)
  controller.unmounted = false;

  SetupFirebaseAuth(controller, params);
  SetupDuckDb(controller, params);
  SetupSocket(controller, params);
}

// เก็บกวาดเฉพาะของที่ผูกกับ Me ตัวนี้ (listener ที่เรียก controller.setState)
// ส่วน DuckDB กับ socket connection เป็น 1 ตัวทั้ง app ปล่อยไว้ให้ mount รอบหน้าใช้ซ้ำ
export const WillUnmount: Handler = async (controller, params) => {
  controller.unmounted = true;
  // ทิ้งผลของ GetDatasetList/GetChartInfo ที่ยังค้างอยู่ ไม่ให้ resolve มาทับรอบ mount ถัดไป
  controller.datasetListSeq++;

  try {
    controller.authUnsubscribe?.();
  } catch (err) {
    console.warn("auth unsubscribe failed", err);
  }
  controller.authUnsubscribe = undefined;

  try {
    if (controller.socketMessageHandler) {
      controller.data.socket?.off('message', controller.socketMessageHandler);
    }
  } catch (err) {
    console.warn("socket off failed", err);
  }
  controller.socketMessageHandler = undefined;
}

export const Login: Handler = async (controller, params) => {
  const auth = getAuth(controller.app);
  const provider = new GoogleAuthProvider();
  await signInWithPopup(auth, provider);
}

export const Logout: Handler = async (controller, params) => {
  const auth = getAuth(controller.app);
  await signOut(auth);
  controller.user = null;
  controller.setState({ loggedin: false });
}

const SetupFirebaseAuth: Handler = async (controller, params) => {
  let auth = getAuth(controller.app);

  const onFirebaseAuthStateChanged = async (user: any) => {
    if (user) {
      // console.log(user.email)
      auth = getAuth(controller.app);
      controller.user = auth.currentUser;
      controller.setState({ loggedin: true });
      // ischart host โหลด dataset อัตโนมัติตอน login (IHV3 โหลดผ่าน flow organization ของตัวเองใน Me.tsx)
      if (controller.isIschart) {
        GetDatasetList(controller, { isAdmin: controller.config?.isAdmin });
      }
    } else {
      console.log("no user");
    }
  }

  // listener ปิด closure ทับ controller ตัวนี้แล้วเรียก setState -> 1 ตัวต่อ Me, ต้องถอดตอน unmount
  controller.authUnsubscribe = onAuthStateChanged(auth, onFirebaseAuthStateChanged);
}

// DuckDB = 1 ตัวทั้ง app: เป็น compute engine ล้วน ๆ ไม่ผูกกับ component จึงแชร์ instance เดียว
// ข้าม Me ได้ ไม่ต้อง terminate ตอน unmount
// ทุก operation ต้องแตะเฉพาะไฟล์และตารางของตัวเอง (ตั้งชื่อไม่ให้ชนแล้ว dropFile ทีหลัง)
// ห้ามใช้ dropFiles() ที่ล้างทั้งหมด เพราะจะลบของคำขออื่นที่กำลังทำงานอยู่
// เก็บเป็น promise ไม่ใช่แค่เช็ค duckInstance เพราะ Me สอง instance อาจ mount ซ้อนกันตอน instantiate() ยังไม่เสร็จ
let duckSetupPromise: Promise<{
  duckInstance: duckdb.AsyncDuckDB,
  duckCon: duckdb.AsyncDuckDBConnection,
}> | null = null;

const createDuckDb = async () => {
  // Set up duckdb -----------------------------------------------------
  const JSDELIVR_BUNDLES = duckdb.getJsDelivrBundles();
  const bundle = await duckdb.selectBundle(JSDELIVR_BUNDLES);
  const worker_url = URL.createObjectURL(
    new Blob([`importScripts("${bundle.mainWorker!}");`], { type: 'text/javascript' })
  );
  const worker = new Worker(worker_url);
  const logger = new duckdb.ConsoleLogger();
  let duckInstance = new duckdb.AsyncDuckDB(logger, worker);
  await duckInstance.instantiate(bundle.mainModule, bundle.pthreadWorker);
  URL.revokeObjectURL(worker_url);

  // const MANUAL_BUNDLES: duckdb.DuckDBBundles = {
  //   mvp: {
  //       mainModule: `/duckdb/duckdb-mvp.wasm`,
  //       mainWorker: `/duckdb/duckdb-browser-mvp.worker.js`,
  //   },
  //   eh: {
  //       mainModule: `/duckdb/duckdb-eh.wasm`,
  //       mainWorker: `/duckdb/duckdb-browser-eh.worker.js`,
  //   },
  // };
  // const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);
  // // Instantiate the asynchronous version of DuckDB-Wasm
  // const worker = new Worker(bundle.mainWorker!);
  // const logger = new duckdb.ConsoleLogger();
  // const duckInstance = new duckdb.AsyncDuckDB(logger, worker);
  // await duckInstance.instantiate(bundle.mainModule, bundle.pthreadWorker);

  let duckCon = await duckInstance.connect();
  return { duckInstance, duckCon };
}

const SetupDuckDb: Handler = async (controller, params) => {
  if (!duckSetupPromise) {
    duckSetupPromise = createDuckDb().catch((err) => {
      duckSetupPromise = null;   // ล้มแล้วให้ mount รอบหน้าลองสร้างใหม่ได้
      throw err;
    });
  }

  let duck: { duckInstance: duckdb.AsyncDuckDB, duckCon: duckdb.AsyncDuckDBConnection };
  try {
    duck = await duckSetupPromise;
  } catch (err) {
    console.error("SetupDuckDb failed:", err);
    return;
  }

  controller.data.duckInstance = duck.duckInstance;
  controller.data.duckCon = duck.duckCon;
  // Me ตัวใหม่เริ่มจาก duckLoaded:false เสมอ -> ต้อง sync ทุกครั้งแม้จะ reuse instance เดิม
  controller.setState({ duckLoaded: true });
}

const SetupSocket: Handler = async (controller, params) => {
  if (!controller.data.socket) {
    controller.data.socket = io('http://localhost:30000/', {
      transports: ['websocket'], // Force WebSocket transport
      reconnectionAttempts: 5, // Number of reconnection attempts
      reconnectionDelay: 1000, // Delay between reconnections
    });
    controller.data.socket.on('connect', () => {
      console.log("connected");
    });
  }

  // connection แชร์ทั้ง app แต่ handler ผูกกับ controller ตัวนี้ -> ต้อง register นอก if
  // (ไม่งั้น Me ตัวที่ 2 เป็นต้นไปจะข้าม block ข้างบนแล้วไม่ได้ listener เลย) และถอดออกตอน unmount
  controller.socketMessageHandler = (message: any) => {
    controller.setState({
      socketMessage: {
        ...message,
        message: (controller.getState().socketMessage?.message || "") + (message?.message || "")
      }
    });
  };
  controller.data.socket.on('message', controller.socketMessageHandler);
}

export const DidUpdate: Handler = async (controller, params) => {
}

export const GetDatasetList: Handler = async (controller, params) => {
  console.log('GetDatasetList params: ', params);
  // ทุกครั้งที่ขอใหม่ = ทิ้งผลของรอบก่อน (เช่น onAuthStateChanged ยิงซ้อนกันบนหน้า ischart standalone)
  const seq = ++controller.datasetListSeq;
  // ผู้เรียกบางทางไม่มี isAdmin ติดมาด้วย (เช่นปุ่ม retry ใน empty state) -> ถอยไปใช้ค่าจาก config
  const isAdmin = params?.isAdmin ?? controller.config?.isAdmin;
  let datasetList: any[] = [];
  let allDivisionRead = true

  let auth = getAuth()
  let uid = auth?.currentUser?.uid
  if (!uid) {
    return
  }
  try {

    if (!controller.useCloud) {
      // local (ischart): ดึงรายชื่อ dataset จาก index บน disk /data/dataset.json (source of truth ของเครื่อง)
      const res = await fetch('/data/dataset.json');
      const idx = res.ok ? await res.json() : { items: [] };
      datasetList = (idx.items ?? []).map((it: any) => it?.id ?? it);
    } else {
      datasetList = controller.config?.ISCHART_DATASET || [];
    }
    // console.log('GetDatasetList datasetList: ', datasetList);

    // Stable, locale-safe sort by id
    // datasetList.sort((a: any, b: any) =>
    //   String(a.id).localeCompare(String(b.id), undefined, { numeric: true })
    // );

    if (seq !== controller.datasetListSeq) return;
    controller.setState({ datasetList, allDivisionRead });

    // keep your downstream call
    await GetChartInfo(controller, { datasetList, ...params, isAdmin, seq });
  } catch (err: any) {
    console.error("GetDatasetList failed:", err);
    // Optional: surface a minimal error state for UI
    if (seq !== controller.datasetListSeq) return;
    controller.setState({ datasetList: [] });
  }
};

export const queryDuck = async (sql: string, con: duckdb.AsyncDuckDBConnection | null) => {
  if (!con) return [];
  let res = await con.query(sql);
  let output = (res?.toArray() || []).map((item: any) => item.toJSON());
  return output.map(toPlainRow)
}

/**
 * แปลง BigInt ที่ DuckDB คืนมาให้เป็น number ธรรมดา
 *
 * คอลัมน์ชนิด BIGINT กลายเป็น BigInt ใน JS ซึ่ง JSON.stringify โยน
 * "Do not know how to serialize a BigInt" ทันทีที่ VegaEmbedRoot คำนวณ specKey
 * กระทบ dimension ที่เป็นจำนวนเต็ม เช่น division_id (bplus_diag, mh_appointment, mh_diag)
 * และ is_appointment (mh_encounter, sapiens)
 *
 * และกันปัญหาชนิดไม่ตรงกับค่าที่มาจาก JSON ไว้ล่วงหน้า — lib/category-order.ts เทียบด้วย
 * `${typeof value}:${String(value)}` ถ้าปล่อยเป็น BigInt จะได้ "bigint:1" ซึ่งไม่ match
 * "number:1" ที่มาจาก spec.json หรือ chartinfo (ตอนนี้ยังไม่มี dim ตัวเลขไหนตั้ง
 * _category_orders ไว้ จึงยังไม่แสดงอาการ)
 */
const toPlainRow = (row: any) =>
  Object.fromEntries(
    Object.entries(row || {}).map(([key, value]) => [
      key,
      typeof value === "bigint" ? Number(value) : value,
    ]),
  );

const isRecent = (cur: any) => {
  return (
    cur?.filter?.y === moment().format('YYYY')
    || cur?.filter?.ym === moment().format('YYYY-MM')
  );
}

type AggInfo = {
  [name: string]: {
    one: any[],
    onefilter: Map<string, any>
    two: any[],
    twofilter: Map<string, any>
  }
}

export const GetChartInfo: Handler = async (controller, params) => {
  let { datasetList } = params;
  console.log("GetChartInfo params: ", params);
  let organization = params?.organization
  // console.log('GetChartInfo organization: ', organization);
  // let email = params?.email
  let isAdmin = params?.isAdmin
  // ถูกเรียกตรง ๆ (ไม่ผ่าน GetDatasetList) ก็ยังต้องจองลำดับของตัวเอง ไม่งั้นจะถูกมองว่า stale ทันที
  const seq = params?.seq ?? ++controller.datasetListSeq;
  const isStale = () => seq !== controller.datasetListSeq;

  if (!datasetList) return console.log("No datasetList supplied");

  let chartInfo: any = {};
  const filtered = datasetList
    .filter((dataset: string) => {
      if (controller.config?.COMPANY === "BDMS") {
        let ret =
          ((dataset.startsWith("bplus_ga_") || dataset.startsWith("bplus_journey_")) &&
            dataset.includes(`_${organization}_`)) ||
          (!dataset.startsWith("bplus_ga") && !dataset.startsWith("bplus_journey"));
        return ret;
      } else if (controller.config?.COMPANY === "MEHEALTH") {
        // Add logic is admin
        if (isAdmin) { 
          return true
        }     
        let ret = dataset.startsWith("mehey_") || dataset.startsWith("mh_");
        return ret;
      } else {
        return false;
      }
    });

  if (!controller.useCloud) {
    // ischart local (เครื่องนี้): โชว์ 'ทุก' dataset จาก index — ไม่พึ่ง config/company/permission
    // (chartinfo.json แต่ละไฟล์ใหญ่มาก รวมหลายร้อย MB จึงวาง placeholder ว่างไว้ แล้ว lazy-load เฉพาะตัวที่เปิด)
    datasetList.forEach((dataset: any) => {
      const datasetId = dataset?.id || dataset;
      if (datasetId) chartInfo[datasetId] = {};
    });
    chartInfo = Object.fromEntries(
      Object.entries(chartInfo).sort((a: any, b: any) => (a?.[0] < b?.[0] ? -1 : 1)),
    );
    if (isStale()) return;
    controller.setState({ chartInfo: { ...chartInfo } });
    return;
  }

  // filter แล้วไม่เหลือ dataset เลย -> ไม่มี setState ข้างล่างเกิดขึ้น chartInfo ของรอบก่อนจะค้างถาวร
  if (filtered.length === 0) {
    if (!isStale()) controller.setState({ chartInfo: {} });
    return;
  }

  filtered
    .map(async (dataset: any) => {
      // console.log("GetChartInfo dataset: ", dataset);
      const datasetId = dataset?.id || dataset; // dataset.id (ischart), dataset (ihv3)

      // console.log("ISCHART_INFORMATICS_GS: ", ISCHART_INFORMATICS_GS);
      // console.log("datasetId: ", datasetId);
      let infoUrl: string | void;
      if (controller.useCloud) {
        const storageRef = ref(
          controller.storage,
          `${informaticsGs(controller)}/${datasetId}/chartinfo.json`,
        );
        infoUrl = await getDownloadURL(storageRef).catch((error) => {
          // A full list of error codes is available at
          // https://firebase.google.com/docs/storage/web/handle-errors
          switch (error.code) {
            case "storage/object-not-found":
              console.log("storage/object-not-found");
              // File doesn't exist

              break;
            case "storage/unauthorized":
              console.log("storage/unauthorized");
              // User doesn't have permission to access the object
              break;
            case "storage/canceled":
              console.log("storage/canceled");
              // User canceled the upload
              break;

            // ...

            case "storage/unknown":
              console.log("storage/unknown");
              // Unknown error occurred, inspect the server response
              break;
          }
        });
      } else {
        infoUrl = `/data/${datasetId}/chartinfo.json`;
      }

      if (!infoUrl) {
        return;
      }

      let chartInfoItem: any;
      try {
        const response = await fetch(infoUrl);
        if (!response.ok) {
          console.warn(`Failed to fetch chartinfo.json: ${response.status} ${response.statusText}`);
          return;
        }
        chartInfoItem = await response.json();
      } catch (error) {
        console.warn("Error fetching or parsing chartinfo.json:", error);
        return;
      }
      console.log("GetChartInfo chartInfoItem: ", chartInfoItem);

      chartInfo[datasetId] = chartInfoItem;
      console.log("GetChartInfo chartInfo: ", chartInfo);
      chartInfo = Object.fromEntries(
        Object.entries(chartInfo).sort((a: any, b: any) => (a?.[0] < b?.[0] ? -1 : 1)),
      );
      if (isStale()) return;
      controller.setState({ chartInfo: { ...chartInfo } });
    });
};

// โหลด chartinfo.json ของ dataset เดียว (lazy) — ใช้ตอนผู้ใช้เปิด/เลือก dataset ในโหมด local
export const LoadChartInfoOne: Handler = async (controller, params) => {
  const datasetId = params?.datasetId;
  if (!datasetId) return;
  const existing = controller.getState().chartInfo?.[datasetId];
  if (existing && Object.keys(existing).length > 0) return; // โหลดแล้ว ข้าม

  let infoUrl: string | void;
  if (controller.useCloud) {
    const storageRef = ref(
      controller.storage,
      `${informaticsGs(controller)}/${datasetId}/chartinfo.json`,
    );
    infoUrl = await getDownloadURL(storageRef).catch(() => undefined);
  } else {
    infoUrl = `/data/${datasetId}/chartinfo.json`;
  }
  if (!infoUrl) return;

  try {
    const response = await fetch(infoUrl);
    if (!response.ok) return;
    const item = await response.json();
    const chartInfo = { ...(controller.getState().chartInfo || {}), [datasetId]: item };
    controller.setState({ chartInfo });

    // ถ้า dataset นี้ถูกเลือกอยู่และยังไม่มี tab ที่ถูกต้อง -> ตั้ง tab แรกให้
    const state = controller.getState();
    if (state.datasetId === datasetId) {
      const tabs = getDatasetTabs(datasetId, chartInfo);
      if (!state.selectedTab || !tabs.includes(state.selectedTab)) {
        controller.setState({ selectedTab: defaultDatasetTab(datasetId, chartInfo) });
      }
    }
  } catch (e) {
    console.warn("LoadChartInfoOne failed:", datasetId, e);
  }
};

// นับคำขอ GetChartData เพื่อแยกไฟล์/ตารางของแต่ละคำขอ และรู้ว่าคำขอไหนคือคำขอล่าสุด
let chartDataSeq = 0;

/**
 * ดึงไฟล์แล้วยืนยันว่า response สำเร็จจริงก่อนแปลงเป็น ArrayBuffer
 *
 * ถ้าไม่เช็ค เมื่อ path ผิดหรือไฟล์หาย local mode จะได้หน้า HTML 404 กลับมาแล้วส่งเข้า
 * registerFileBuffer ทำให้ DuckDB โยน "No magic bytes found at end of file" ซึ่งไม่ได้บอกเลย
 * ว่าปัญหาจริงคือไฟล์ไม่มี
 */
const fetchArrayBuffer = async (url: string, label: string, init?: RequestInit) => {
  const response = await fetch(url, init);
  if (!response.ok) {
    throw new Error(`โหลด ${label} ไม่สำเร็จ (${response.status} ${response.statusText})`);
  }
  return response.arrayBuffer();
};

export const GetChartData: Handler = async (controller, params) => {
  console.log("GetChartData params: ", params);
  // Get parquet file ----------------------------------------------------
  let { duckInstance, duckCon } = controller.data;
  let {
    filename,
    datasetId,
    dimList,
    setData,
    setForExcel,
    setSpec,
    agg,
    prefix,
  } = params;
  if (
    !duckInstance ||
    !duckCon ||
    !datasetId ||
    !dimList ||
    !setData ||
    !setSpec ||
    !agg
  )
    return console.log(
      "Insufficient params.",
      duckInstance,
      duckCon,
      dimList,
      setData,
    );
  // ชื่อ token ต่อคำขอ ทำให้สองคำขอที่ซ้อนกันไม่แย่งไฟล์และตารางกัน
  // เดิมใช้ dropFiles() ล้างทั้งหมด + ตาราง chartdata ชื่อเดียว คำขอที่มาทีหลังจึงลบไฟล์
  // ของคำขอก่อนหน้าทิ้งกลางคัน แล้ว query เดิมพังด้วย "No magic bytes found at end of file"
  // ชื่อสั้นและไม่มีอักขระพิเศษ จึงไม่ต้องกังวลเรื่อง quote ในชื่อไฟล์ที่มี ' หรือ |
  const token = ++chartDataSeq;
  const regName = `chartdata_${token}.parquet`;
  const tableName = `chartdata_${token}`;

  let spec: any;
  let info: ArrayBuffer;
  if (controller.useCloud) {
    // const storage = getStorage(controller.app);

    // Get chart data
    const chartDataRef = ref(
      controller.storage,
      `${informaticsGs(controller)}/${datasetId}${prefix}/chartdata/${filename}.parquet`,
    );
    console.log("Chart data path:", `${informaticsGs(controller)}/${datasetId}${prefix}/chartdata/${filename}.parquet`);
    const infoUrl = await getDownloadURL(chartDataRef);
    info = await fetchArrayBuffer(infoUrl, `${filename}.parquet`);

    // Get spec
    const specRef = ref(
      controller.storage,
      `${informaticsGs(controller)}/${datasetId}/spec.json`,
    );
    const specUrl = await getDownloadURL(specRef);
    spec = await (await fetch(specUrl)).json();
  } else {
    const dataUrl = `/data/${datasetId}${prefix}/chartdata/${filename}.parquet`;
    console.log("Chart data path:", dataUrl);
    info = await fetchArrayBuffer(dataUrl, `${filename}.parquet`);
    spec = await (await fetch(`/data/${datasetId}/spec.json`)).json();
  }
  setSpec(spec);

  await duckInstance.registerFileBuffer(regName, new Uint8Array(info));

  // Query data ----------------------------------------------------------
  try {
    await duckCon.query(`
      create or replace table ${tableName} as
      select ${dimList.join(",")}, ${agg}
      from read_parquet('${regName}');`);

    console.log("filename", filename);
    let output = await queryDuck(`from ${tableName}`, duckCon);
    console.log("output", output);
    let forExcel: any[] = [];
    if (dimList.length === 1) {
      let pivotSql = `pivot ${tableName} on ${dimList[0]} using last(${agg})`;
      forExcel = await queryDuck(pivotSql, duckCon);
    } else if (dimList.length === 2) {
      let pivotSql = `
      pivot ${tableName}
        on ${dimList[0]}
        using last(${agg})
        group by ${dimList[1]}
        order by ${dimList[1]}`;
      forExcel = await queryDuck(pivotSql, duckCon);
      const groupOrder = getCategoryDomain(
        spec,
        dimList[1],
        output.map((row: any) => row[dimList[1]]),
      );
      forExcel = sortRowsByCategoryOrder(forExcel, dimList[1], groupOrder);
    }
    forExcel = forExcel.map((row: any) => {
      return Object.fromEntries(
        Object.entries(row).map((entry: any) => [
          entry[0],
          entry[1] === null ? 0 : entry[1],
        ]),
      );
    });
    if (forExcel.length > 0) {
      forExcel = [Object.keys(forExcel[0])].concat(
        forExcel.map((row: any) => Object.values(row)),
      );
    }

    // Set state ---------------------------------------------------------
    // คำขอที่มาทีหลังคือสิ่งที่ผู้ใช้เลือกล่าสุด ผลของคำขอเก่าที่เพิ่งมาถึงจึงต้องทิ้ง
    // ไม่งั้นกราฟจะกระพริบกลับไปเป็นข้อมูลชุดก่อน
    if (token !== chartDataSeq) return;
    setData(
      output.map((item: any) => ({
        ...item,
        [agg]: Number(item[agg]),
      })),
    );
    setForExcel(forExcel);
  } finally {
    await duckCon.query(`drop table if exists ${tableName};`);
    await duckInstance.dropFile(regName);
  }
};

export const GetCustomChartData: Handler = async (controller, params) => {
  let { duckInstance, duckCon } = controller.data;
  let { filename, datasetId, setData, prefix = "" } = params;
  if (!duckInstance || !duckCon || !datasetId || !setData)
    return console.log("Insufficient params for custom chart.");

  let tableName = `custom_${filename.replace(/[^a-zA-Z0-9_]/g, '_')}`;

  let info: ArrayBuffer;
  if (controller.useCloud) {
    const chartDataRef = ref(controller.storage, `${informaticsGs(controller)}/${datasetId}${prefix}/custom_chartdata/${filename}.parquet`);
    const infoUrl = await getDownloadURL(chartDataRef);
    info = await (await fetch(infoUrl)).arrayBuffer();
  } else {
    info = await (await fetch(`/data/${datasetId}${prefix}/custom_chartdata/${filename}.parquet`)).arrayBuffer();
  }

  await duckCon.query(`drop table if exists ${tableName};`);
  await duckInstance.registerFileBuffer(`${filename}.parquet`, new Uint8Array(info));

  await duckCon.query(`create or replace table ${tableName} as select * from read_parquet('${filename}.parquet');`);
  let output = await queryDuck(`from ${tableName}`, duckCon);

  output = output.map(row =>
    Object.fromEntries(
      Object.entries(row).map(([k, v]) => [k, typeof v === "bigint" ? Number(v) : v])
    )
  );

  setData(output);
}

export const GetGaDashboardData: Handler = async (controller, params) => {
  let { duckInstance, duckCon } = controller.data;
  let { datasetId, setUserActivity, setTopCountries, setOsData, setPageViews } = params;
  if (!duckInstance || !duckCon || !datasetId) return;

  // Fetch one parquet from cloud or local storage
  const fetchParquet = async (table: string): Promise<ArrayBuffer> => {
    if (controller.useCloud) {
      const chartDataRef = ref(controller.storage, `${informaticsGs(controller)}/${datasetId}/main/${table}.parquet`);
      const url = await getDownloadURL(chartDataRef);
      return (await fetch(url)).arrayBuffer();
    }
    return (await fetch(`/data/${datasetId}/main/${table}.parquet`)).arrayBuffer();
  };

  // Coerce BigInt → Number (same pattern as GetCustomChartData)
  const normalize = (rows: any[]) =>
    rows.map(row => Object.fromEntries(
      Object.entries(row).map(([k, v]) => [k, typeof v === "bigint" ? Number(v) : v])
    ));

  // ลบเฉพาะไฟล์ของ widget ตัวเอง — dropFiles() จะล้างไฟล์ของกราฟที่ GetChartData
  // ลงทะเบียนค้างอยู่ด้วย ทำให้ query ที่กำลังทำงานพังกลางคัน
  const registerOwn = async (name: string, buf: ArrayBuffer) => {
    await duckInstance.dropFile(name).catch(() => undefined); // ยังไม่เคยลงทะเบียนก็ไม่เป็นไร
    await duckInstance.registerFileBuffer(name, new Uint8Array(buf));
  };


  // Widget 1: daily active users, last 30 days — ym__ymd.parquet
  {
    const buf = await fetchParquet("ym__ymd");
    await registerOwn("ga_ymd.parquet", buf);
    await duckCon.query(`CREATE OR REPLACE TABLE ga_ymd AS
      SELECT ymd AS date, user_id__distinct AS users
      FROM read_parquet('ga_ymd.parquet')
      WHERE ymd >= CAST(CURRENT_DATE - INTERVAL 30 DAYS AS VARCHAR)
      ORDER BY ymd`);
    const rows = normalize((await duckCon.query("FROM ga_ymd")).toArray().map((r: any) => r.toJSON()));
    setUserActivity(rows as UserActivityRow[]);
  }

  // Widget 2: top countries on most-recent available day — country__ym__ymd.parquet
  // Uses MAX(ymd) so pipeline lag doesn't produce empty results
  {
    const buf = await fetchParquet("country__ym__ymd");
    await registerOwn("ga_ctry.parquet", buf);
    await duckCon.query(`CREATE OR REPLACE TABLE ga_ctry AS
      SELECT country, user_id__distinct AS users
      FROM read_parquet('ga_ctry.parquet')
      WHERE ymd = (SELECT MAX(ymd) FROM read_parquet('ga_ctry.parquet'))
      ORDER BY users DESC LIMIT 10`);
    const rows = normalize((await duckCon.query("FROM ga_ctry")).toArray().map((r: any) => r.toJSON()));
    setTopCountries(rows as CountryRow[]);
  }

  // Widget 3: active users by OS, last 6 months — operating_system__ym.parquet
  {
    const buf = await fetchParquet("operating_system__ym");
    await registerOwn("ga_os.parquet", buf);
    await duckCon.query(`CREATE OR REPLACE TABLE ga_os AS
      SELECT operating_system AS os, ym, user_id__distinct AS users
      FROM read_parquet('ga_os.parquet')
      WHERE operating_system IS NOT NULL
        AND ym >= STRFTIME(CURRENT_DATE - INTERVAL 180 DAYS, '%Y-%m')
      ORDER BY os, ym`);
    const rows = normalize((await duckCon.query("FROM ga_os")).toArray().map((r: any) => r.toJSON()));
    setOsData(rows as OsRow[]);
  }

  // Widget 4: page views by feature, last 6 months — feature__ym.parquet
  {
    const buf = await fetchParquet("feature__ym");
    await registerOwn("ga_feat.parquet", buf);
    await duckCon.query(`CREATE OR REPLACE TABLE ga_feat AS
      SELECT COALESCE(feature, '(not set)') AS page, SUM(user_id__distinct) AS users
      FROM read_parquet('ga_feat.parquet')
      WHERE feature IS NOT NULL
        AND ym >= STRFTIME(CURRENT_DATE - INTERVAL 180 DAYS, '%Y-%m')
      GROUP BY feature ORDER BY users DESC LIMIT 10`);
    const rows = normalize((await duckCon.query("FROM ga_feat")).toArray().map((r: any) => r.toJSON()));
    setPageViews(rows as PageViewRow[]);
  }
};

export const GetTemplateList: Handler = async (controller, params) => {
  const templateRef = collection(controller.db, "IFT_template");
  const result = await getDocs(templateRef);
  let templateList = result.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
  controller.setState({ templateList });
}

export const AddTemplate: Handler = async (controller, params) => {
  let { template } = params;
  if (!template) return console.log("Insufficient params");
  const templateRef = collection(controller.db, "IFT_template");
  await addDoc(templateRef, template);
}

export const GenInformatics: Handler = async (controller, params) => {
  let { duckInstance, duckCon } = controller.data;
  let { arrayBuffer, setProgress, setImageUrl, template } = params;
  if (!duckInstance || !duckCon || !arrayBuffer || !setProgress)
    return console.log("No duck or insufficient params");
  if (!template) {
    const templateDocRef = doc(controller.db, "IFT_template", "cnmi");
    const templateDoc = await getDoc(templateDocRef);
    template = templateDoc.data();
  }
  await duckInstance.registerFileBuffer('base.parquet', new Uint8Array(arrayBuffer));
  await duckCon.query(`
    create or replace table base as 
    from read_parquet('base.parquet');`);
  // let result = await query(`from base select count(*)`, duckCon);
  let sequence = await runPipeline(template, duckCon, setProgress);
  let data = await queryDuck(`from ${sequence[0]}`, duckCon);
  GenerateThumbnail(data, sequence[0], setImageUrl);
}

const GenerateThumbnail = async (data: any, dim: string, setImageUrl: any) => {
  data = data.map((item: any) => ({ ...item, id__count: Number(item.id__count) }));
  let temp = document.createElement("div")
  let res = await embed(temp, {
    data: { values: data },
    title: "Encounter Count",
    width: 900,
    height: 500,
    mark: "bar",
    encoding: {
      x: {
        field: dim,
        type: "ordinal",
        sort: { field: dim, order: "ascending" }
      },
      y: {
        field: "id__count",
        type: "quantitative"
      },
    }
  });
  let url = await res.view.toImageURL("png");
  setImageUrl(url);
  // DownloadImage(url, temp);
}

const DownloadImage = (url: string, temp: any) => {
  let link = document.createElement("a");
  link.href = url;
  link.download = "image_download.png";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  temp.remove();
}

export const GetFields: Handler = async (controller, params) => {
  let { duckInstance, duckCon } = controller.data;
  let { templateId, setFields } = params;
  if (!duckInstance || !duckCon || !templateId || !setFields)
    return console.log("No duck or insufficient params");

  const storageRef = ref(controller.storage, `${studioGs(controller)}/template/${templateId}/fields.parquet`);
  const downloadUrl = await getDownloadURL(storageRef);

  let fields = await (await fetch(downloadUrl)).arrayBuffer();
  await duckInstance.registerFileBuffer('fields.parquet', new Uint8Array(fields));
  await duckCon.query(`
    create or replace table fields as 
    from read_parquet('fields.parquet');`);
  let res = await queryDuck("from fields", duckCon);
  setFields(res);
}

export const GetRecipe: Handler = async (controller, params) => {
  let { recipeId, setRecipe } = params;
  if (!recipeId || !setRecipe) return console.log("Insufficient params");

  const recipeDocRef = doc(controller.db, "IFT_recipe", recipeId);
  const recipeDoc = await getDoc(recipeDocRef);
  const data = recipeDoc.data();
  setRecipe(data);
}

export const AddRecipeItem: Handler = async (controller, params) => {
  let { recipeId, item, setRecipe } = params;
  if (!recipeId || !item || !setRecipe) return console.log("Insufficient params");


  const recipeDocRef = doc(controller.db, "IFT_recipe", recipeId);
  const recipeDoc = await getDoc(recipeDocRef);
  let data = recipeDoc.data() || { items: [] };
  data.items.push(item);

  await updateDoc(recipeDocRef, { items: data.items || [] });
  setRecipe(data);
}

// ---------------------------------------------------------------------------
// MeLab Report — แท็บ custom ที่ pipeline ประกาศผ่าน chartinfo.reportTab
// (UI ห้ามเทียบชื่อ dataset — dataset ไหนมี reportTab ก็ได้หน้านี้)
//
// โหลด report_manifest.json + parquet ทุก sheet จาก custom_chartdata/ ครั้งเดียว
// ต่อ dataset แล้วเปิดเป็น view ชื่อตาม manifest (service/receipt/orders/promotion)
// SQL ประกอบฝั่ง MelabReport.tsx ตามสัญญา __TOKEN__ ใน manifest — สูตรเดียวกับ
// jobs_base/melab_group/manifest.py fill() ห้ามแก้ข้างเดียว

let melabReportReady: string | null = null;
// single-flight: กันโหลดซ้อน (effect ยิงสองรอบ/builtAt เปลี่ยนถี่) — เรียกซ้อนให้รอรอบที่วิ่งอยู่
let melabReportLoading: Promise<void> | null = null;
// ชื่อไฟล์รุ่นปัจจุบันของแต่ละ view — ไว้ลบรุ่นเก่าหลังสลับ view ครบแล้วเท่านั้น
const melabReportRegNames = new Map<string, string>();

export const GetMelabReportBundle: Handler = async (controller, params) => {
  const { duckInstance, duckCon } = controller.data;
  const { datasetId, setBundle, setError } = params;
  if (!duckInstance || !duckCon || !datasetId || !setBundle) return;

  const fileUrl = async (path: string) => {
    if (controller.useCloud) {
      const fileRef = ref(controller.storage,
        `${informaticsGs(controller)}/${datasetId}/${path}`);
      return await getDownloadURL(fileRef);
    }
    return `/data/${datasetId}/${path}`;
  };

  try {
    // no-store ทั้ง manifest และ parquet — pipeline rebuild ได้ระหว่างวัน ถ้าปล่อยให้ browser
    // cache ผู้ใช้จะเห็น SQL/ข้อมูลรุ่นเก่าโดยไม่มีอะไรฟ้อง (เจอจริง: วันที่เป็น epoch ค้าง
    // เพราะ manifest รุ่นก่อนแก้ strftime ติด cache)
    const manifestUrl = await fileUrl("custom_chartdata/report_manifest.json");
    const res = await fetch(manifestUrl, { cache: "no-store" });
    if (!res.ok) {
      throw new Error(`โหลด report_manifest.json ไม่สำเร็จ (${res.status})`);
    }
    const manifest = await res.json();

    // ลงทะเบียน parquet ใหม่เมื่อ dataset เปลี่ยน "หรือ" builtAt เปลี่ยน (rebuild ระหว่างเปิดหน้า)
    // ⚠️ ห้ามกลับไปแบบ drop-ก่อน-register ทีละชีต: การ์ดที่กำลัง query จะเจอไฟล์หายชั่ววูบ
    // ("IO Error: No files found ... melabreport_receipt.parquet" — เจอจริง 2026-08-31
    // ตอน pipeline rebuild ระหว่างหน้าเปิดอยู่) จึงลงทะเบียนชื่อใหม่ตามรุ่น builtAt ให้ครบ
    // ทุกชีตก่อน แล้วสลับ view ทีเดียว ค่อยลบไฟล์รุ่นเก่าเป็นขั้นสุดท้าย
    const readyKey = `${datasetId}|${manifest.builtAt ?? ""}`;
    while (melabReportReady !== readyKey) {
      if (!melabReportLoading) {
        melabReportLoading = (async () => {
          const ver = String(manifest.builtAt ?? "0").replace(/[^0-9]/g, "");
          const regs: { view: string; regName: string; buf: ArrayBuffer }[] = [];
          for (const sheet of Object.values<any>(manifest.sheets || {})) {
            const buf = await fetchArrayBuffer(
              await fileUrl(`custom_chartdata/${sheet.file}`), sheet.file, { cache: "no-store" });
            regs.push({
              view: sheet.view,
              regName: `melabreport_${sheet.view}_${ver}.parquet`,
              buf,
            });
          }
          for (const r of regs) {
            // ชื่อรุ่นเดียวกันอาจค้างจากรอบที่พังกลางคัน — ลบเฉพาะชื่อของตัวเองเท่านั้น
            // (dropFiles() ล้างทั้งหมดจะพังกราฟอื่นที่กำลัง query — ดู GetChartData)
            await duckInstance.dropFile(r.regName).catch(() => undefined);
            await duckInstance.registerFileBuffer(r.regName, new Uint8Array(r.buf));
          }
          for (const r of regs) {
            await duckCon.query(
              `CREATE OR REPLACE VIEW ${r.view} AS SELECT * FROM read_parquet('${r.regName}')`);
          }
          // ทุก view ชี้รุ่นใหม่แล้ว — query เก่าที่ยังวิ่งบนไฟล์รุ่นก่อนอ่านจบก่อนถึงบรรทัดนี้
          for (const r of regs) {
            const prev = melabReportRegNames.get(r.view);
            if (prev && prev !== r.regName) {
              await duckInstance.dropFile(prev).catch(() => undefined);
            }
            melabReportRegNames.set(r.view, r.regName);
          }
          melabReportReady = readyKey;
        })().finally(() => { melabReportLoading = null; });
      }
      await melabReportLoading;
    }
    setBundle(manifest);
  } catch (e: any) {
    setError?.(String(e?.message ?? e));
  }
}

export const QueryMelabReport: Handler = async (controller, params) => {
  const { duckCon } = controller.data;
  const { sql, setRows, setError } = params;
  if (!duckCon || !sql || !setRows) return;
  try {
    setRows(await queryDuck(sql, duckCon));
  } catch (e: any) {
    setError?.(String(e?.message ?? e));
  }
}
